import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarIcon, Download, Mail, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errText } from "@/lib/api";
import { downloadSheetPdf, openPrintSheet } from "@/lib/exportSheet";
import { heatClass, isoDate, longDate, parseISO, shortDate } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

const STAT_LABELS = [
  ["total", "Listings"],
  ["fixed_time", "Fixed-time"],
  ["session_based", "Session"],
  ["morning", "Morning"],
  ["non_court", "Non-court"],
  ["post_court", "Post-court"],
  ["out_of_station", "Out-of-station"],
  ["clashes", "Clash advisories"],
  ["placeholders", "Placeholders"],
  ["matters", "Matters touched"],
];
export default function Overview() {
  const { workspace, isOwner, role } = useAuth();
  const [span, setSpan] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (role === "clerk") return;
    try {
      const { data: d } = await api.get("/overview", {
        params: { span, date: isoDate(anchor) },
      });
      setData(d);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [span, anchor, workspace?.workspace_id, role]);

  useEffect(() => {
    load();
  }, [load]);

  const maxTotal = useMemo(
    () => Math.max(1, ...(data?.days || []).map((d) => d.total)),
    [data],
  );

  const emailIt = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/reminders/send-overview", null, { params: { span } });
      toast.success(res.emailed ? "Overview emailed to you" : "Overview generated (email is off)");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps">Schedule overview</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">
            {span === "week" ? "This week" : "This month"}
          </h1>
          {data ? (
            <p className="mt-3 text-sm text-muted-foreground">
              {longDate(data.start)} — {longDate(data.end)}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={span} onValueChange={setSpan}>
            <TabsList className="rounded-sm">
              <TabsTrigger value="week" data-testid="overview-span-week" className="rounded-sm px-4 text-base">
                Week
              </TabsTrigger>
              <TabsTrigger value="month" data-testid="overview-span-month" className="rounded-sm px-4 text-base">
                Month
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="overview-date-picker" className="rounded-sm h-11 font-normal">
                <CalendarIcon className="h-4 w-4 mr-2" /> {shortDate(isoDate(anchor))}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              <Calendar mode="single" selected={anchor} onSelect={(d) => d && setAnchor(d)} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          data-testid="overview-print-btn"
          onClick={() => openPrintSheet(span, isoDate(anchor))}
          className="rounded-full h-11"
        >
          <Printer className="h-4 w-4 mr-2" /> Print sheet
        </Button>
        <Button
          variant="outline"
          data-testid="overview-pdf-btn"
          onClick={() =>
            downloadSheetPdf(span, isoDate(anchor)).catch((e) => toast.error(errText(e)))
          }
          className="rounded-full h-11"
        >
          <Download className="h-4 w-4 mr-2" /> Download PDF
        </Button>
        {isOwner ? (
          <Button
            data-testid="overview-email-btn"
            onClick={emailIt}
            disabled={sending}
            className="rounded-full h-11"
          >
            <Mail className="h-4 w-4 mr-2" /> {sending ? "Sending…" : "Email this to me"}
          </Button>
        ) : null}
      </div>

      {data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overview-stats">
            {STAT_LABELS.map(([key, label]) => (
              <div key={key} className="surface p-5" data-testid={`overview-stat-${key}`}>
                <p className="label-caps">{label}</p>
                <p className="font-heading text-3xl font-bold mt-2">{data.totals[key] ?? 0}</p>
              </div>
            ))}
          </div>

          {data.totals.busiest_day ? (
            <p className="text-sm text-muted-foreground" data-testid="overview-busiest">
              Busiest day: <strong className="text-foreground">{longDate(data.totals.busiest_day)}</strong>
            </p>
          ) : null}

          <div className="surface overflow-x-auto" data-testid="overview-table">
            <table className="w-full text-left min-w-[46rem]">
              <thead>
                <tr className="border-b border-border">
                  {["Date", "Load", "Total", "Session 1", "Session 2", "Fixed", "Travel", "Clashes"].map((h) => (
                    <th key={h} className="px-4 py-3 label-caps whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.days.map((d) => (
                  <tr key={d.date} className="border-b border-border" data-testid={`overview-row-${d.date}`}>
                    <td className="px-4 py-3 text-base font-semibold whitespace-nowrap">
                      {shortDate(d.date)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`block h-3 rounded-sm border border-border ${heatClass(
                          d.total === 0 ? 0 : Math.min(4, Math.ceil((d.total / maxTotal) * 4)),
                        )}`}
                        style={{ width: `${Math.max(8, (d.total / maxTotal) * 100)}%` }}
                      />
                    </td>
                    <td className="px-4 py-3 text-base">{d.total}</td>
                    <td className="px-4 py-3 text-base">{d.session_1}</td>
                    <td className="px-4 py-3 text-base">{d.session_2}</td>
                    <td className="px-4 py-3 text-base">{d.fixed_time}</td>
                    <td className="px-4 py-3 text-base">{d.out_of_station}</td>
                    <td className="px-4 py-3">
                      {d.clashes ? <span className="chip chip-fixed">{d.clashes}</span> : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="h-48 surface animate-pulse" data-testid="overview-loading" />
      )}
    </div>
  );
}
